import { ResourceType, Subscription } from '@content-api/api/subscription';
import { useTranslation } from 'react-i18next';
import { Separator } from '@ui/components';
import { cn } from '@ui/utils';
import { FC } from 'react';

import { SubscriptionFeaturesList } from './subscription-card/subscription-features-list';
import { SubscriptionPricing } from './subscription-card/subscription-pricing';
import { SubscriptionBilling } from './subscription-card/subscription-billing';
import { SubscriptionHeader } from './subscription-card/subscription-header';
import { SubscriptionFooter } from './subscription-card/subscription-footer';

export type SubscriptionCardProps = {
  numOfEmployees?: number;
  numOfVehicles?: number;

  onSelect?: () => void;
  isSelected?: boolean;
  isLoading?: boolean;
  isCurrent?: boolean;
  disabled?: boolean;
  className?: string;
} & Partial<Subscription>;

export const SubscriptionCard: FC<SubscriptionCardProps> = (props) => {
  const { t } = useTranslation();

  const {
    subscriptionFeatures,
    learnMoreLabel,
    numOfEmployees,
    numOfVehicles,
    billingPeriod,
    learnMoreUrl,
    costPerUnit,
    billingUnit,
    isSelected,
    isCurrent,
    className,
    isLoading,
    onSelect,
    disabled,
    code,
    name,
  } = props;

  const calculatedUnit =
    (billingUnit === ResourceType.EMPLOYEE && ResourceType.EMPLOYEE) ||
    (billingUnit === ResourceType.VEHICLE && ResourceType.VEHICLE) ||
    ((numOfEmployees || 0) > (numOfVehicles || 0) ? ResourceType.EMPLOYEE : ResourceType.VEHICLE);

  return (
    <div
      className={cn(
        'h-full flex flex-col min-w-96 max-w-96 min-h-172 border-2 border-background rounded-xxl bg-background overflow-hidden',
        {
          'border-surface': isSelected,
        },
        className,
      )}
    >
      <div className="px-6 pt-6 flex flex-col h-full">
        <SubscriptionHeader isCurrent={isCurrent} isLoading={isLoading} name={name} />
        <SubscriptionPricing
          billingUnit={calculatedUnit}
          costPerUnit={costPerUnit}
          isLoading={isLoading}
        />
        <Separator className="my-6" />
        <SubscriptionFeaturesList
          features={subscriptionFeatures}
          isLoading={isLoading}
          className="flex-1"
          code={code}
        />
        <Separator className="my-6" />
        <SubscriptionBilling billingPeriod={billingPeriod} isLoading={isLoading} />
        <Separator className="my-6" />
        <SubscriptionFooter
          learnMoreLabel={learnMoreLabel}
          learnMoreUrl={learnMoreUrl}
          isLoading={isLoading}
        />
      </div>

      <button
        className={cn(
          'text-surface bg-background outline-none p-2 w-full mt-8 cursor-pointer hover:bg-surface-weak font-semibold',
          disabled && 'opacity-50 pointer-events-none',
          isSelected && 'bg-surface text-contrast pointer-events-none',
          isLoading && 'opacity-0 pointer-events-none',
        )}
        onClick={onSelect}
      >
        {t(isSelected ? 'SUBSCRIPTIONS.SELECTED' : 'SUBSCRIPTIONS.SELECT')}
      </button>
    </div>
  );
};
